import { Badge, Box, Button, Group, Paper, Stack, Text, TextInput } from "@mantine/core"
import { useEffect, useRef, useState } from "react"
import { IconArrowLeft, IconSearch, IconUsers, appIcon } from "atlas_forecasting_app/components/common/icons"
import { apiFetch } from "atlas_forecasting_app/lib/api_fetch"
import { formatCurrencyFromCents, formatDateTime } from "atlas_forecasting_app/lib/format"
import { RouterLink } from "atlas_forecasting_app/lib/router"
import { CenteredMessage, EmptyState, ErrorAlert, LoadingState, PageHeader, SectionCard } from "atlas_forecasting_app/components/common/page_elements"

export function AdminUsersPage({ currentUser }) {
  const isAdmin = !!currentUser?.roles?.includes("admin")
  const [items, setItems] = useState([])
  const [cursor, setCursor] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [query, setQuery] = useState("")
  const [submittedQuery, setSubmittedQuery] = useState("")
  const sentinelRef = useRef(null)

  const resetAndLoad = () => {
    setItems([])
    setCursor(null)
    setError("")
    setSubmittedQuery(query.trim())
  }

  const loadMore = async () => {
    if (!isAdmin || loading) return
    setLoading(true)
    try {
      const qs = new URLSearchParams()
      qs.set("limit", "25")
      if (cursor) qs.set("cursor", cursor)
      if (submittedQuery) qs.set("q", submittedQuery)
      const data = await apiFetch(`/api/admin/users?${qs.toString()}`)
      setItems((prev) => prev.concat(data.users || []))
      setCursor(data.next_cursor || null)
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadMore()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedQuery])

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && cursor) loadMore()
      },
      { rootMargin: "600px 0px" },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [cursor, loading, submittedQuery, isAdmin])

  if (!currentUser) {
    return <CenteredMessage title="Admin" body="Log in required." />
  }

  if (!isAdmin) {
    return <CenteredMessage title="Admin" body="You don’t have access to this page." />
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title="Users"
        subtitle="Search users, inspect balances, and manage roles."
        action={
          <Button component={RouterLink} to="/admin" variant="light" leftSection={appIcon(IconArrowLeft, "inline")}>
            Back to admin
          </Button>
        }
      />

      <SectionCard title="Users" subtitle="Search by email.">
        <Group align="flex-end" gap="sm">
          <TextInput
            style={{ flex: 1, maxWidth: 520 }}
            label="Search users"
            value={query}
            placeholder="Search users..."
            onChange={(event) => setQuery(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") resetAndLoad()
            }}
          />
          <Button type="button" onClick={resetAndLoad} leftSection={appIcon(IconSearch, "inline")}>
            Search
          </Button>
          {submittedQuery ? (
            <Button
              type="button"
              variant="subtle"
              onClick={() => {
                setQuery("")
                setSubmittedQuery("")
                setItems([])
                setCursor(null)
                setError("")
              }}
            >
              Clear
            </Button>
          ) : null}
        </Group>
      </SectionCard>

      <ErrorAlert message={error} />

      {items.length === 0 && !loading && !error ? <EmptyState message="No users found." /> : null}

      <Stack gap="md">
        {items.map((user) => (
          <Paper key={user.id} withBorder radius="md" shadow="xs" p="lg">
            <Group justify="space-between" align="flex-start" gap="md">
              <Stack gap="sm">
                <Stack gap={4}>
                  <Text fw={600}>{user.email || `User #${user.id}`}</Text>
                  <Text size="sm" c="dimmed">
                    {user.email ? user.email : ""}
                    {user.created_at ? ` • created ${formatDateTime(user.created_at)}` : ""}
                  </Text>
                </Stack>
                <Group gap="xs">
                  <Badge variant="light" title="Account balance">
                    {formatCurrencyFromCents(user.account_balance_cents ?? 0)}
                  </Badge>
                  {(user.roles || []).map((role) => (
                    <Badge key={role} variant="light" color="red">
                      {role}
                    </Badge>
                  ))}
                </Group>
              </Stack>
              <Button component={RouterLink} to={`/admin/users/${user.id}`} variant="light" leftSection={appIcon(IconUsers, "inline")}>
                View
              </Button>
            </Group>
          </Paper>
        ))}

        <Box ref={sentinelRef}>{loading ? <LoadingState label="Loading users..." /> : null}</Box>
      </Stack>
    </Stack>
  )
}
